import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import { ASSETS_QUERY_KEY } from "../assets/hooks";
import { FLOORPLAN_QUERY_KEY } from "../common/queryKeys";
import {
    createAssetAssignment,
    releaseAssetAssignment,
    searchAssetAssignments,
} from "./api";
import type { AssetAssignmentCreatePayload, AssetAssignmentView } from "./types";

export const ASSET_ASSIGNMENTS_QUERY_KEY = ["admin", "asset-assignments"] as const;

export interface SearchAssetAssignmentsParams {
    readonly assetId: string;
    readonly userId: string;
    readonly workspaceId: string;
    readonly targetKind: string;
    readonly openOnly: boolean;
}

export function useAssetAssignments(
    params: SearchAssetAssignmentsParams,
): UseQueryResult<readonly AssetAssignmentView[], Error> {
    const client = useApiClient();
    return useQuery({
        queryKey: [
            ...ASSET_ASSIGNMENTS_QUERY_KEY,
            params.assetId,
            params.userId,
            params.workspaceId,
            params.targetKind,
            params.openOnly,
        ],
        queryFn: () =>
            searchAssetAssignments(client, {
                assetId: params.assetId,
                userId: params.userId,
                workspaceId: params.workspaceId,
                targetKind: params.targetKind,
                ...(params.openOnly ? { open: true } : {}),
            }),
        staleTime: 30_000,
    });
}

export function useCreateAssetAssignment(): UseMutationResult<
    AssetAssignmentView,
    Error,
    AssetAssignmentCreatePayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => createAssetAssignment(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

export interface ReleaseInput {
    readonly assetId: string;
    readonly assignmentId: string;
}

export function useReleaseAssetAssignment(): UseMutationResult<
    AssetAssignmentView,
    Error,
    ReleaseInput
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ assetId, assignmentId }) =>
            releaseAssetAssignment(client, assetId, assignmentId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}
